import { useState, type FormEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { ContractTerminationReason } from '@bhops/shared';
import { contractsApi, type TerminateContractBody } from '@/api/contracts';
import { extractApiError } from '@/api/client';
import { Modal } from '@/components/Modal';
import { Button, Field, FormError, Select, Textarea } from '@/components/Form';
import { useToast } from '@/components/Toast';
import { Icon } from '@/components/Icon';
import { invalidateCustomerMasterCascade } from '@/lib/invalidate-customer-master-cascade';

const REASON_LABEL: Record<ContractTerminationReason, string> = {
  EARLY_BREAKOFF: '조기 해지',
  CHURN_CASCADE: '딜 이탈에 따른 해지',
  RENEWAL_REPLACED: '재계약 대체',
  CUSTOMER_REQUEST: '고객사 요청',
  OTHER: '기타',
};

interface Props {
  open: boolean;
  onClose: () => void;
  contractId: string;
  contractHumanId: string;
}

export function ContractTerminateModal({ open, onClose, contractId, contractHumanId }: Props) {
  const toast = useToast();
  const qc = useQueryClient();

  const [reason, setReason] = useState<ContractTerminationReason>(
    ContractTerminationReason.EARLY_BREAKOFF,
  );
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);

  const terminate = useMutation({
    mutationFn: (body: TerminateContractBody) => contractsApi.terminate(contractId, body),
    onSuccess: () => {
      // round4 #5a (BUG#2) — 해지는 활성 배치·매출·고객사 상태에 즉시 영향 →
      // contracts/customer-master/deployments/deals/dashboard 일괄 무효화.
      invalidateCustomerMasterCascade(qc);
      qc.invalidateQueries({ queryKey: ['contracts', contractId] });
      toast.success(`${contractHumanId} 계약을 해지했습니다.`, '계약 해지');
      onClose();
      setReason(ContractTerminationReason.EARLY_BREAKOFF);
      setNote('');
    },
    onError: (err) => setError(extractApiError(err)),
  });

  function submit(e: FormEvent<HTMLFormElement>): void {
    e.preventDefault();
    setError(null);
    terminate.mutate({
      reason,
      note: note || undefined,
    });
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={`${contractHumanId} 계약 해지`}
      width={460}
      footer={
        <>
          <Button type="button" variant="secondary" onClick={onClose}>
            취소
          </Button>
          <Button
            type="submit"
            form="contract-terminate-form"
            variant="danger"
            disabled={terminate.isPending}
          >
            {terminate.isPending ? '처리 중…' : '해지 확인'}
          </Button>
        </>
      }
    >
      <form id="contract-terminate-form" onSubmit={submit} noValidate>
        <FormError>{error}</FormError>

        <div style={warning}>
          <Icon name="alert" size={16} color="var(--fg-danger)" style={{ marginTop: 1 }} /> 해지된
          계약은 복구할 수 없습니다. 운영 중인 로봇 배치와 매출 집계에 바로 반영됩니다.
        </div>

        <Field label="해지 사유" htmlFor="t-reason" required>
          <Select
            id="t-reason"
            value={reason}
            onChange={(e) => setReason(e.target.value as ContractTerminationReason)}
          >
            {Object.entries(REASON_LABEL).map(([k, v]) => (
              <option key={k} value={k}>
                {v}
              </option>
            ))}
          </Select>
        </Field>

        <Field
          label="메모"
          htmlFor="t-note"
          hint="해지 정황이나 책임자, 정산 메모 등을 적어 주세요. 비워 둬도 됩니다."
        >
          <Textarea id="t-note" rows={4} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      </form>
    </Modal>
  );
}

const warning = {
  display: 'flex',
  alignItems: 'flex-start',
  gap: 6,
  padding: 10,
  marginBottom: 14,
  border: '1px solid var(--fg-danger)',
  borderRadius: 'var(--r-card)',
  background: 'var(--bg-danger-subtle)',
  color: 'var(--fg-danger)',
  fontSize: 12,
} as const;
